import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, AlertTriangle, CreditCard, Download, FileText, Flag, Info } from 'lucide-react';

import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { formatDateOnly } from '../utils/dateOnly';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED' | string;

type InvoiceRow = {
  id: string;
  business_id: string;
  appointment_id?: string | null;
  bill_to: string;
  customer_email?: string | null;
  invoice_date: string;
  due_date: string;
  status: InvoiceStatus;
  notes?: string | null;
  services: any;
  sub_total: string | number;
  discount: string | number;
  tax: string | number;
  total: string | number;
  created_at?: string;
  updated_at?: string;
  business?: any;
  onlinePaymentsEnabled?: boolean;
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v: any): string => safeNum(v).toFixed(2);

const toInvoiceLabel = (id: string): string => {
  const short = (id || '').split('-')[0]?.toUpperCase() || '—';
  return `INV-${short}`;
};

const fmtDate = (v: any): string => formatDateOnly(v, 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const parseServices = (raw: any): Array<{ description: string; qty: number; amount: number; unitPrice?: number; professionalName?: string | null }> => {
  let arr: any[] = [];
  try {
    if (Array.isArray(raw)) arr = raw;
    else if (typeof raw === 'string') arr = JSON.parse(raw || '[]');
    else if (raw && typeof raw === 'object') arr = raw;
  } catch {
    arr = [];
  }

  return (Array.isArray(arr) ? arr : []).map((s) => {
    const qty = Math.max(0, safeNum(s?.qty ?? 0));
    const unitPrice = Math.max(0, safeNum(s?.unitPrice ?? 0));
    const included = s?.included !== false; // default true
    const amount = included ? Math.max(0, safeNum(s?.amount ?? qty * unitPrice)) : 0;
    return {
      description: String(s?.description ?? '').trim() || 'Item',
      qty,
      unitPrice,
      amount,
      professionalName: s?.professionalName ?? null,
    };
  });
};

const getBusinessName = (row: InvoiceRow | null): string => {
  const b: any = (row as any)?.business;
  const name = String(b?.name ?? b?.business_name ?? '').trim();
  return name || 'Business';
};

const getBusinessAddressLine = (row: InvoiceRow | null): string => {
  const b: any = (row as any)?.business;
  const address = String(b?.address ?? '').trim();
  const city = String(b?.city ?? '').trim();
  const state = String(b?.state ?? '').trim();
  const zip = String(b?.zip_code ?? b?.zipCode ?? b?.zip ?? '').trim();
  const line2 = [city, state, zip].filter(Boolean).join(', ').replace(/,\s*,/g, ', ').trim();
  return [address, line2].filter(Boolean).join(', ').trim();
};

const statusBadge = (status: InvoiceStatus) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return { label: 'Paid', className: 'bg-green-50 text-green-700 border-green-200' };
  if (s === 'SENT') return { label: 'Unpaid', className: 'bg-amber-50 text-amber-800 border-amber-200' };
  if (s === 'VOIDED') return { label: 'Voided', className: 'bg-gray-50 text-gray-700 border-gray-200' };
  return { label: s || '—', className: 'bg-gray-50 text-gray-700 border-gray-200' };
};

const GuestInvoiceDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const query = useQuery();
  const guestToken = query.get('t') || '';

  const { user, logout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<InvoiceRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const businessName = useMemo(() => getBusinessName(row), [row]);
  const addressLine = useMemo(() => getBusinessAddressLine(row), [row]);

  const items = useMemo(() => parseServices(row?.services).filter((li) => li.amount > 0 || li.qty > 0), [row?.services]);
  const badge = useMemo(() => statusBadge(row?.status || ''), [row?.status]);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    if (!guestToken) {
      setLoading(false);
      setRow(null);
      setError('This invoice link is missing a guest token. Please use the link from your email.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const inv = await apiService.getPublicInvoice(invoiceId, guestToken);
      setRow(inv as any);
    } catch (e: any) {
      console.error('Failed to load public invoice', e);
      setRow(null);
      setError(e?.response?.data?.error || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, guestToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadPdf = useCallback(async () => {
    if (!invoiceId) return;
    if (!guestToken) return;
    try {
      const { blob, filename } = await apiService.downloadPublicInvoicePdf(invoiceId, guestToken);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${toInvoiceLabel(invoiceId)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('Public PDF download failed', e);
      toast.error(e?.response?.data?.error || 'Failed to download PDF');
    }
  }, [invoiceId, guestToken]);

  if (loading) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading invoice…</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="min-h-screen no-animations flex flex-col relative">
        <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Invoice</h1>
            <p className="text-gray-600 mb-6">{error || 'Invoice not found'}</p>
            <button onClick={() => navigate('/')} className="aacore-btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isUnpaid = String(row.status || '').toUpperCase() === 'SENT';
  const onlinePaymentsEnabled = row.onlinePaymentsEnabled === true;

  return (
    <div className="min-h-screen no-animations flex flex-col relative">
      <CustomerNavbar onLogout={logout} showDashboardLink={!!user} showLogoutButton={!!user} showAuthButtons={!user} />

      <div className="flex-1 pt-6 pb-12 overflow-visible">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto overflow-visible">
          <div className="mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center font-semibold"
              style={{ color: '#0B4778' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#0a3d66')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#0B4778')}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Home
            </button>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold mb-0">{toInvoiceLabel(row.id)}</h1>
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${badge.className}`}>
                  {badge.label}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-0">
                {businessName} • Sent {fmtDate(row.invoice_date)} • Due {fmtDate(row.due_date)}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isUnpaid ? (
                <button
                  type="button"
                  className="aacore-btn-primary"
                  disabled={!onlinePaymentsEnabled}
                  title={
                    !onlinePaymentsEnabled
                      ? 'Online payments are not enabled for this business. See the note on this invoice for payment instructions.'
                      : undefined
                  }
                  onClick={() => {
                    if (!onlinePaymentsEnabled) return;
                    navigate(`/guest/invoices/${encodeURIComponent(String(invoiceId))}/pay?t=${encodeURIComponent(guestToken)}`);
                  }}
                >
                  <CreditCard className="w-4 h-4" />
                  Pay now
                </button>
              ) : null}
              <button type="button" className="aacore-btn-outlined" onClick={handleDownloadPdf}>
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button type="button" className="aacore-btn-outlined" onClick={() => toast('Issue reporting coming soon')}>
                <Flag className="w-4 h-4" />
                Report an issue
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            <div className="lg:col-span-5 bg-white border border-gray-200 rounded-2xl p-4">
              <div className="space-y-3">
                <div>
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                    Business
                  </div>
                  <div className="font-semibold text-gray-900">{businessName}</div>
                  {addressLine ? <div className="text-sm text-gray-600">{addressLine}</div> : null}
                </div>

                <div>
                  <div className="text-gray-500 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                    Billed to
                  </div>
                  <div className="font-semibold text-gray-900">{String(row.bill_to || '').trim() || '—'}</div>
                  {row.customer_email ? <div className="text-sm text-gray-600">{String(row.customer_email).trim()}</div> : null}
                </div>

                {row.notes ? (
                  <div>
                    <div className="text-gray-500 text-xs font-bold uppercase tracking-wider" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                      Note
                    </div>
                    <div className="text-sm text-gray-900 whitespace-pre-line">{String(row.notes).trim()}</div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="lg:col-span-7 bg-white border border-gray-200 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-semibold text-gray-900">Invoice items</div>
                <div className="text-sm text-gray-600">
                  Invoice total: <span className="font-bold text-gray-900">${formatMoney(row.total)}</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-400">
                      <th className="py-2">Item</th>
                      <th className="py-2 text-right w-20">Qty</th>
                      <th className="py-2 text-right w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.length === 0 ? (
                      <tr>
                        <td className="py-3 text-sm text-gray-600" colSpan={3}>
                          No line items
                        </td>
                      </tr>
                    ) : (
                      items.map((it, idx) => (
                        <tr key={idx}>
                          <td className="py-3">
                            <div className="font-semibold text-gray-900">{it.description}</div>
                            {it.professionalName ? <div className="text-sm text-gray-600">{it.professionalName}</div> : null}
                          </td>
                          <td className="py-3 text-right text-sm text-gray-700">{it.qty}</td>
                          <td className="py-3 text-right text-sm font-semibold text-gray-900">${formatMoney(it.amount)}</td>
                        </tr>
                      ))
                    )}
                    <tr>
                      <td className="py-3 text-right font-bold text-gray-900" colSpan={2}>
                        Total
                      </td>
                      <td className="py-3 text-right font-bold text-gray-900">${formatMoney(row.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {onlinePaymentsEnabled ? (
                <div className="mt-3 p-3 border border-gray-200 rounded-xl bg-gray-50 text-sm text-gray-600 flex gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                  <div>Payments are processed securely by Stripe. If something looks off, contact the business before paying.</div>
                </div>
              ) : (
                <div
                  role="alert"
                  className="mt-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-amber-900"
                >
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  </div>
                  <div className="min-w-0 text-xs leading-relaxed text-amber-900">
                    <p className="text-sm font-semibold text-amber-950">Online payments are not enabled</p>
                    <p className="mt-1">
                      {businessName} has not set up online payments through AACore Booking. You will not be able to pay this
                      invoice online through the platform.
                    </p>
                    <p className="mt-2">Please see the note on this invoice for payment instructions from the business.</p>
                  </div>
                </div>
              )}

              {!user ? (
                <div className="mt-3 text-xs text-gray-500">
                  Have an AACore Booking account?{' '}
                  <Link to="/login" className="text-[#0B4778] font-semibold hover:text-[#0a3d66]">
                    Sign in
                  </Link>{' '}
                  to manage your invoices in your dashboard.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GuestInvoiceDetailsPage;

